import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { TablesInsert, TablesUpdate } from "@/lib/supabase";
import type { BrandingScopeType, TenantBranding } from "@/lib/branding";

export type TenantBrandingInsert = TablesInsert<"tenant_branding">;
export type TenantBrandingUpdate = TablesUpdate<"tenant_branding">;

// =============================================================================
// Liste de tous les branding overrides (admin)
// =============================================================================
export function useTenantBrandings() {
  return useQuery({
    queryKey: ["admin", "branding"] as const,
    queryFn: async (): Promise<TenantBranding[]> => {
      const { data, error } = await supabase
        .from("tenant_branding")
        .select("*")
        .eq("is_active", true)
        .order("scope_type", { ascending: true })
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as TenantBranding[];
    },
  });
}

// =============================================================================
// Branding effectif (cascade) pour un salarié — via RPC SECURITY DEFINER
// =============================================================================
/** Sous-ensemble des colonnes `tenant_branding` renvoyé par la RPC (snake_case). */
export type ResolvedBranding = Partial<
  Pick<
    TenantBranding,
    | "product_name"
    | "product_tagline"
    | "brand"
    | "brand_foreground"
    | "brand_hover"
    | "brand_muted"
    | "accent"
    | "accent_foreground"
    | "logo_url"
    | "logo_dark_url"
    | "favicon_url"
    | "font_sans"
    | "font_display"
    | "footer_legal"
  >
>;

export function useResolvedBrandingForSalarie(salarieId: string | null | undefined) {
  return useQuery({
    queryKey: ["branding", "resolved", "salarie", salarieId ?? "none"] as const,
    enabled: Boolean(salarieId),
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<ResolvedBranding> => {
      if (!salarieId) return {};
      const { data, error } = await supabase.rpc("branding_resolve_for_salarie", {
        p_salarie_id: salarieId,
      });
      if (error) throw error;
      return (data ?? {}) as ResolvedBranding;
    },
  });
}

export function useResolvedBrandingForSociete(societeId: string | null | undefined) {
  return useQuery({
    queryKey: ["branding", "resolved", "societe", societeId ?? "none"] as const,
    enabled: Boolean(societeId),
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<ResolvedBranding> => {
      if (!societeId) return {};
      const { data, error } = await supabase.rpc("branding_resolve_for_societe", {
        p_societe_id: societeId,
      });
      if (error) throw error;
      return (data ?? {}) as ResolvedBranding;
    },
  });
}

// =============================================================================
// Branding pour un scope spécifique (renvoie 0 ou 1 ligne)
// =============================================================================
export function useBrandingForScope(args: {
  scope_type: BrandingScopeType | null;
  scope_id: string | null;
}) {
  const { scope_type, scope_id } = args;
  return useQuery({
    queryKey: ["admin", "branding", "scope", { scope_type, scope_id }] as const,
    enabled: Boolean(scope_type && scope_id),
    queryFn: async (): Promise<TenantBranding | null> => {
      const { data, error } = await supabase
        .from("tenant_branding")
        .select("*")
        .eq("scope_type", scope_type!)
        .eq("scope_id", scope_id!)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as TenantBranding | null;
    },
  });
}

// =============================================================================
// Upsert (INSERT ou UPDATE selon présence d'un row pour ce scope)
// =============================================================================
export function useUpsertTenantBranding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: TenantBrandingInsert): Promise<TenantBranding> => {
      // L'unicité (scope_type, scope_id) garantit l'upsert. On utilise
      // la résolution naturelle de Supabase via `upsert(..., onConflict: ...)`.
      const { data, error } = await supabase
        .from("tenant_branding")
        .upsert(input, { onConflict: "scope_type,scope_id" })
        .select("*")
        .single();
      if (error) throw error;
      return data as TenantBranding;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "branding"] });
    },
  });
}

// =============================================================================
// Suppression (hard delete)
// =============================================================================
export function useDeleteTenantBranding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from("tenant_branding").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "branding"] });
    },
  });
}

// =============================================================================
// Désactivation (soft) — flag `is_active = false`
// =============================================================================
export function useDeactivateTenantBranding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase
        .from("tenant_branding")
        .update({ is_active: false })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "branding"] });
    },
  });
}
